/**
 * What a CA firm would pay in a year, and what the same jobs cost bought separately. The sums behind the sales deck's
 * calculator and its printed examples. Our figures come from prices.ts through planTotal, the same sum as the Pricing
 * page, so the deck cannot quote another price. Other companies' figures follow rivals.ts's rules: their own page where
 * we could read it, "reported" otherwise, always dated; re-check them with CHECKED_ON before a deploy weeks later.
 */
import { PRICES, planTotal, STORAGE_GB_MONTH, AI_PER_MESSAGE, type ProductKey } from '../prices.ts';

export const CHECKED_ON = '1 November 2026';
/** Same conversion as rivals.ts. */
const USD_INR = 90;

export interface Firm {
  staff: number;
  clients: number;
  /** Clients on monthly GST. */
  gst: number;
  /** Income-tax returns a year. */
  itr: number;
  /** WhatsApp a month: updates, letters and reminders (utility templates). */
  updates: number;
  /** WhatsApp a month: replies inside the chat (service messages). */
  replies: number;
  /** WhatsApp a month: greetings and offers (marketing templates). */
  offers: number;
  /** WhatsApp numbers the firm connects. */
  numbers: number;
  teams: boolean;
  workbook: boolean;
  hr: boolean;
  /** What an hour of staff time costs the firm, in rupees. */
  hourly: number;
}

export const FIRMS: Record<'small' | 'mid' | 'large', Firm & { label: string }> = {
  small: { label: 'Small firm', staff: 3, clients: 40, gst: 20, itr: 40, updates: 70, replies: 300, offers: 10, numbers: 1, teams: true, workbook: false, hr: false, hourly: 150 },
  mid: { label: 'Growing firm', staff: 10, clients: 150, gst: 100, itr: 150, updates: 250, replies: 900, offers: 50, numbers: 1, teams: true, workbook: true, hr: false, hourly: 150 },
  large: { label: 'Large firm', staff: 25, clients: 500, gst: 300, itr: 500, updates: 800, replies: 2500, offers: 150, numbers: 1, teams: true, workbook: true, hr: true, hourly: 150 },
};

export interface Rates { marketing: number; utility: number }

/** The service messages a number gets free each month before Meta charges them, at the utility rate. */
const FREE_SERVICE = 1000;
/** Storage assumptions, said under the calculator. */
const MB_PER_CLIENT_YEAR = 100;
const YEARS_KEPT = 3;
/** Files a client sends a year, and the share no pattern reads (masked AI, only if the firm switches it on). */
const FILES_PER_CLIENT_YEAR = 20;
const AI_SHARE = 0.05;

/** From the worked example on the CA page, in minutes: 100 monthly clients take 49 hours today and 3 to 4 hours with Jackdesk. */
const MINUTES = { gstToday: 29.4, gstWith: 2.2, itrToday: 12, itrWith: 2 };

/** The practice tools and rates we compare with. */
export const RIVAL = {
  zoho: { name: 'Zoho Practice Standard', month: 2950, included: 5, extraMonth: 400, source: 'vendor', url: 'https://www.zoho.com/practice/pricing' },
  interakt: { name: 'Interakt Growth', year: 26870, source: 'vendor', url: 'https://www.interakt.shop/pricing/', utility: 0.15, marketing: 0.958, service: 0.128 },
  slack: { name: 'Slack Pro', month: 245.25, source: 'vendor', url: 'https://slack.com/intl/en-in/pricing' },
  empmonitor: { name: 'EmpMonitor', usdMonth: 3.83, source: 'reported', url: 'https://www.capterra.in/software/201459/empmonitor' },
  greythr: { name: 'greytHR Essential', month: 2495, included: 50, extraMonth: 45, source: 'vendor', url: 'https://greythr.com/pricing' },
  icai: { name: 'ICAI Simplify PMS', freeUpTo: 5, month: 100, source: 'vendor', url: 'https://bs.icai.org/?p=1630' },
  taxdome: { name: 'TaxDome Essentials', usdYear: 800, source: 'reported', url: 'https://www.capterra.com/p/186749/TaxDome/pricing/' },
} as const;

export interface StackRow { job: string; tool: string; year: number; reported: boolean }

export interface Estimate {
  plan: number;
  messages: number;
  messagesBeforeFree: number;
  free: number;
  storage: number;
  ai: number;
  total: number;
  perClientMonth: number;
  stack: StackRow[];
  stackTotal: number;
  saved: number;
  times: number;
  hoursToday: number;
  hoursWith: number;
  hoursBack: number;
  hoursValue: number;
}

const plan = (key: ProductKey, n: number) => PRICES[key].plans.find((p) => p.n >= n) ?? PRICES[key].plans[PRICES[key].plans.length - 1];

export const zohoYear = (staff: number) => (RIVAL.zoho.month + Math.max(0, staff - RIVAL.zoho.included) * RIVAL.zoho.extraMonth) * 12;
export const taxdomeYear = (staff: number) => RIVAL.taxdome.usdYear * USD_INR * staff;
export const icaiYear = (staff: number) => (staff > RIVAL.icai.freeUpTo ? staff * RIVAL.icai.month * 12 : 0);

export function estimate(f: Firm, rates: Rates): Estimate {
  const items: { key: ProductKey; n: number }[] = [{ key: 'messaging', n: f.staff }, { key: 'ca', n: f.staff }];
  if (f.teams) items.push({ key: 'teams', n: f.staff });
  if (f.workbook) items.push({ key: 'workbook', n: f.staff });
  if (f.hr) items.push({ key: 'hr', n: f.staff });
  const year = planTotal(items).year;

  const paidReplies = Math.max(0, f.replies - FREE_SERVICE * f.numbers);
  const messagesBeforeFree = 12 * (f.updates * rates.utility + f.offers * rates.marketing + paidReplies * rates.utility);
  const free = plan('ca', f.staff).waYear;
  const messages = Math.max(0, messagesBeforeFree - free);
  const includedGb = plan('ca', f.staff).gb;
  const storedGb = (f.clients * MB_PER_CLIENT_YEAR * YEARS_KEPT) / 1024;
  const storage = Math.max(0, storedGb - includedGb) * STORAGE_GB_MONTH * 12;
  const ai = f.clients * FILES_PER_CLIENT_YEAR * AI_SHARE * AI_PER_MESSAGE;
  const total = Math.round(year + messages + storage + ai);

  const { interakt, slack, empmonitor, greythr, zoho } = RIVAL;
  const stack: StackRow[] = [
    { job: 'Jobs and client work', tool: zoho.name, year: zohoYear(f.staff), reported: false },
    { job: 'WhatsApp inbox', tool: interakt.name, year: interakt.year, reported: false },
    { job: 'The same messages', tool: 'at their rates', year: Math.round(12 * (f.updates * interakt.utility + f.offers * interakt.marketing + paidReplies * interakt.service)), reported: false },
  ];
  if (f.teams) stack.push({ job: 'Team chat', tool: slack.name, year: Math.round(slack.month * f.staff * 12), reported: false });
  if (f.workbook) stack.push({ job: 'Attendance and time', tool: empmonitor.name, year: Math.round(empmonitor.usdMonth * USD_INR * f.staff * 12), reported: true });
  if (f.hr) stack.push({ job: 'HR and payroll', tool: greythr.name, year: (greythr.month + Math.max(0, f.staff - greythr.included) * greythr.extraMonth) * 12, reported: false });
  const stackTotal = stack.reduce((s, r) => s + r.year, 0);

  const hoursToday = (f.gst * MINUTES.gstToday + (f.itr * MINUTES.itrToday) / 12) / 60;
  const hoursWith = (f.gst * MINUTES.gstWith + (f.itr * MINUTES.itrWith) / 12) / 60;
  const hoursBack = hoursToday - hoursWith;

  return {
    plan: year, messages: Math.round(messages), messagesBeforeFree: Math.round(messagesBeforeFree), free, storage: Math.round(storage), ai: Math.round(ai), total,
    perClientMonth: f.clients ? Math.round(total / f.clients / 12) : 0,
    stack, stackTotal, saved: stackTotal - total, times: total ? stackTotal / total : 0,
    hoursToday, hoursWith, hoursBack, hoursValue: Math.round(hoursBack * 12 * f.hourly),
  };
}
